import asset_resource_hashes from '../config/asset-resource-hashes.json';
import storage_asset_object_keys from '../config/storage-asset-object-keys.json';

const asset_resource_hash_map = asset_resource_hashes as Record<string, string>;
const storage_asset_object_key_map = storage_asset_object_keys as Record<
  string,
  string
>;
const STORAGE_PUBLIC_BASE_URL =
  'https://ekko-design-tos-1320720418.cos.ap-guangzhou.myqcloud.com';
const LOCAL_PREVIEW_HOST_NAMES = new Set([
  'localhost',
  '127.0.0.1',
  '::1',
  '0.0.0.0',
]);

export type AssetResourceSourceUrls = {
  fallback_source_url: string | null;
  source_url: string;
};

export function get_asset_resource_source_urls(
  resource_file_name: string,
): AssetResourceSourceUrls {
  return get_asset_resource_source_urls_for_host_name(
    resource_file_name,
    get_current_host_name(),
  );
}

export function get_asset_resource_source_urls_for_host_name(
  resource_file_name: string,
  host_name: string | null,
): AssetResourceSourceUrls {
  const local_source_url = create_local_asset_resource_url(resource_file_name);

  if (
    !should_use_local_preview_source(host_name) &&
    is_storage_backed_asset_resource(resource_file_name)
  ) {
    return {
      fallback_source_url: local_source_url,
      source_url: create_storage_asset_resource_url(resource_file_name),
    };
  }

  return {
    fallback_source_url: null,
    source_url: local_source_url,
  };
}

export function get_asset_resource_url(resource_file_name: string) {
  return get_asset_resource_source_urls(resource_file_name).source_url;
}

function create_storage_asset_resource_url(resource_file_name: string) {
  const object_key = storage_asset_object_key_map[resource_file_name];

  if (!object_key) {
    return create_local_asset_resource_url(resource_file_name);
  }

  const storage_asset_resource_url = `${STORAGE_PUBLIC_BASE_URL}/${encode_storage_object_key_path(
    object_key,
  )}`;

  return append_asset_resource_version_query({
    resource_file_name,
    resource_url: storage_asset_resource_url,
  });
}

function create_local_asset_resource_url(resource_file_name: string) {
  const resource_file_hash = asset_resource_hash_map[resource_file_name];

  if (!resource_file_hash) {
    return `/resources/${encodeURIComponent(resource_file_name)}`;
  }

  return `/resources/${resource_file_hash}/${encodeURIComponent(
    resource_file_name,
  )}`;
}

function is_storage_backed_asset_resource(resource_file_name: string) {
  return Boolean(storage_asset_object_key_map[resource_file_name]);
}

function get_current_host_name() {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.location.hostname;
}

function should_use_local_preview_source(host_name: string | null) {
  return host_name !== null && LOCAL_PREVIEW_HOST_NAMES.has(host_name);
}

function encode_storage_object_key_path(object_key: string) {
  return object_key
    .split('/')
    .map(path_segment => encodeURIComponent(path_segment))
    .join('/');
}

function append_asset_resource_version_query({
  resource_file_name,
  resource_url,
}: {
  resource_file_name: string;
  resource_url: string;
}) {
  const resource_file_hash = asset_resource_hash_map[resource_file_name];

  if (!resource_file_hash) {
    return resource_url;
  }

  return `${resource_url}?v=${encodeURIComponent(resource_file_hash)}`;
}
